// TestimonialPromptModal — post-quiz 5-star reaction capture.
// Fires only for celebration moments (pct >= 80). Ratings 5 + opt-in send the
// quote into the founder's pending queue on the admin dashboard.
import { useState } from "react";
import { Star, X, Send, Sparkles, Check } from "lucide-react";
import { submitTestimonial } from "@/lib/api";
import { toast } from "sonner";

export const TestimonialPromptModal = ({ open, onClose, lecture, pct, defaultDisplayName = "" }) => {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [quote, setQuote] = useState("");
  const [displayName, setDisplayName] = useState(defaultDisplayName);
  const [role, setRole] = useState("");
  const [permission, setPermission] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const canSubmit = quote.trim().length >= 12 && rating >= 1;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const res = await submitTestimonial({
        lecture_id: lecture?.id,
        rating,
        quote: quote.trim(),
        display_name: displayName.trim(),
        role: role.trim(),
        permission,
      });
      setDone(true);
      if (res?.queued) {
        toast.success("Sent — Nicole reviews every quote personally. Thank you.");
      } else {
        toast.success("Thanks — feedback saved.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit right now.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="testimonial-prompt-modal"
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-background shadow-2xl border border-border"
      >
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-3 py-1 text-xs font-bold uppercase tracking-widest">
              <Sparkles className="h-3.5 w-3.5" /> {pct >= 80 ? "You crushed it" : "Quick reaction?"}
            </div>
            <h2 className="mt-2 font-serif text-2xl font-bold">
              {done ? "Thank you." : "How did that go?"}
            </h2>
            {!done && (
              <p className="mt-1 text-sm text-muted-foreground">
                One honest line about your study session. If it's great and you opt in, Nicole may feature you on the site.
              </p>
            )}
          </div>
          <button
            data-testid="testimonial-close"
            onClick={onClose}
            className="rounded-full p-2 hover:bg-secondary/60 transition"
            aria-label="Close feedback prompt"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <span className="inline-grid place-items-center h-14 w-14 rounded-2xl bg-emerald-100 text-emerald-700">
              <Check className="h-7 w-7" />
            </span>
            <p className="mt-4 font-serif text-lg font-semibold">Feedback received.</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {rating >= 4 && permission
                ? "Nicole reviews every quote before publishing. You'll get an email if it goes live."
                : "Your feedback goes straight to the founder. Nothing published."}
            </p>
            <button
              data-testid="testimonial-close-done"
              onClick={onClose}
              className="mt-6 inline-flex rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-bold"
            >
              Back to my grade
            </button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="testimonial-form" className="p-6 space-y-4">
            {/* Star rating */}
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Your rating</label>
              <div className="mt-2 flex gap-1" data-testid="testimonial-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`testimonial-star-${n}`}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)}
                    aria-label={`${n} star${n === 1 ? "" : "s"}`}
                    className="p-1"
                  >
                    <Star
                      className={`h-8 w-8 transition ${
                        (hover || rating) >= n
                          ? "fill-amber-400 text-amber-400"
                          : "text-muted-foreground/40"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="tp-quote" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">One line about your session</label>
              <textarea
                id="tp-quote" data-testid="testimonial-quote"
                value={quote} onChange={(e) => setQuote(e.target.value)}
                rows={3}
                maxLength={600}
                required
                placeholder='e.g. "I actually understood chapter 5 for the first time — got an A on the quiz."'
                className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm resize-none"
              />
              <p className="mt-1 text-[11px] text-muted-foreground text-right">{quote.length}/600</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="tp-name" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">How to credit you</label>
                <input
                  id="tp-name" data-testid="testimonial-name" type="text"
                  value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Sam R."
                  maxLength={40}
                  className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label htmlFor="tp-role" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Grade / role</label>
                <input
                  id="tp-role" data-testid="testimonial-role" type="text"
                  value={role} onChange={(e) => setRole(e.target.value)}
                  placeholder="11th grade · CA"
                  maxLength={60}
                  className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>

            <label className="flex items-start gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox" data-testid="testimonial-permission"
                checked={permission} onChange={(e) => setPermission(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Lectura can feature this quote on the site with the name and role above. You can email hello@getlectura.app anytime to remove it.
              </span>
            </label>

            <button
              type="submit"
              data-testid="testimonial-submit"
              disabled={!canSubmit || submitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-bold hover:opacity-90 transition disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {submitting ? "Sending…" : "Send to founder"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default TestimonialPromptModal;
